/**
 * PassKeeper 主应用组件
 */

import React, { useEffect, useState } from "react";
import { useStore } from "./store";
import { getItems } from "./api";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import AddItem from "./components/AddItem";
import ItemModal from "./components/ItemModal";
import Toast from "./components/Toast";
import { CATEGORY_NAMES } from "@passkeeper/shared";
import type { PasswordCategory } from "@passkeeper/shared";

export default function App() {
  const {
    isLoggedIn,
    items,
    setItems,
    setIsLoading,
    decryptItems,
    masterPassword,
    selectedCategory,
    searchQuery,
    editingItem,
    setEditingItem,
    viewingItem,
    setViewingItem,
  } = useStore();

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const [showAdd, setShowAdd] = useState(false);

  const showToast = (message: string, type: "success" | "error" | "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 加载密码条目
  const loadItems = async () => {
    setIsLoading(true);
    const result = await getItems({
      category: selectedCategory !== "all" ? (selectedCategory as PasswordCategory) : undefined,
      q: searchQuery || undefined,
    });
    if (result.ok && result.data) {
      setItems(result.data);
    } else {
      showToast(result.error || "加载失败", "error");
    }
    setIsLoading(false);
  };

  // 首次加载 + 搜索/分类变更时重新加载
  useEffect(() => {
    if (isLoggedIn) {
      loadItems();
    }
  }, [isLoggedIn, selectedCategory, searchQuery]);

  // 加载完成后自动解密
  useEffect(() => {
    if (items.length > 0 && masterPassword) {
      decryptItems();
    }
  }, [items, masterPassword]);

  if (!isLoggedIn) {
    return (
      <>
        <Login showToast={showToast} />
        {toast && <Toast message={toast.message} type={toast.type} />}
      </>
    );
  }

  return (
    <>
      {/* 顶部导航 */}
      <header className="layout-header">
        <div className="logo">
          <svg viewBox="0 0 100 100">
            <rect x="10" y="10" width="80" height="80" rx="16" fill="#3b82f6"/>
            <rect x="25" y="45" width="50" height="40" rx="8" fill="white"/>
            <circle cx="50" cy="65" r="8" fill="#3b82f6"/>
            <rect x="48" y="65" width="4" height="12" fill="#3b82f6"/>
            <path d="M50 20 Q40 25 40 35 V45 H60 V35 Q60 25 50 20Z" fill="none" stroke="white" stroke-width="5"/>
          </svg>
          PassKeeper
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            + 添加密码
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => useStore.getState().logout()}
          >
            退出
          </button>
        </div>
      </header>

      <div className="main-content">
        <Dashboard showToast={showToast} onAddClick={() => setShowAdd(true)} />
      </div>

      {/* 添加/编辑模态框 */}
      {showAdd && (
        <AddItem
          showToast={showToast}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            loadItems();
          }}
        />
      )}

      {editingItem && (
        <AddItem
          editItem={editingItem}
          showToast={showToast}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            loadItems();
          }}
        />
      )}

      {viewingItem && (
        <ItemModal
          item={viewingItem}
          showToast={showToast}
          onClose={() => setViewingItem(null)}
          onEdit={() => {
            setViewingItem(null);
            setEditingItem(viewingItem);
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </>
  );
}